import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, formatDateTime, formatTime } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Clock,
  User,
  Calendar,
  AlertCircle,
  CheckCircle2,
  MapPin,
  History,
  Edit,
  X,
  Coffee,
  LogIn,
  LogOut,
} from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { TimePicker } from "../components/ui/time-picker";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const createPunchIcon = (color = "#3B82F6") => {
  return L.divIcon({
    className: "punch-marker",
    html: `<div style="
      background-color: ${color};
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 0 10px rgba(59, 130, 246, 0.6);
    "></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

export const PunchRecords = () => {
  const { t } = useTranslation();
  const { user, isAdmin, isRevendedor, isSupervisor } = useAuth();
  const [workdays, setWorkdays] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [workSchedules, setWorkSchedules] = useState([]);
  const [timeRecords, setTimeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedWorkday, setSelectedWorkday] = useState(null);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [mapLocation, setMapLocation] = useState(null);
  const [mapTitle, setMapTitle] = useState("");
  const [isAdjustDialogOpen, setIsAdjustDialogOpen] = useState(false);
  const [adjustFormData, setAdjustFormData] = useState({
    clock_in: "",
    lunch_out: "",
    lunch_in: "",
    clock_out: "",
    notes: "",
  });

  const canSelectCompany = () => isAdmin() || isRevendedor();
  const canEdit = () => isAdmin() || isRevendedor() || isSupervisor();

  // Establecer fechas por defecto (últimos 30 días)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    if (!startDate) {
      setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    }
    if (!endDate) {
      setEndDate(today.toISOString().split('T')[0]);
    }
  }, []);

  const fetchWorkdays = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (selectedUser && selectedUser !== "all") {
        params.append("user_id", selectedUser);
      }
      if (startDate) {
        params.append("start_date", startDate);
      }
      if (endDate) {
        params.append("end_date", endDate);
      }
      
      const url = params.toString() 
        ? `${API_URL}/time-records/workdays?${params.toString()}`
        : `${API_URL}/time-records/workdays`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      const workdaysData = Array.isArray(response.data) ? response.data : [];
      
      // Enriquecer con información de usuarios y horarios
      const enrichedWorkdays = await Promise.all(
        workdaysData.map(async (workday) => {
          const userData = users.find(u => u.id === workday.user_id);
          const schedule = workSchedules.find(s => s.id === userData?.work_schedule_id);
          
          return {
            ...workday,
            user: userData,
            schedule: schedule,
          };
        })
      );
      
      // Agrupar por fecha y usuario para detectar múltiples jornadas
      const grouped = {};
      enrichedWorkdays.forEach(wd => {
        const key = `${wd.date}_${wd.user_id}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(wd);
      });
      
      // Ordenar por fecha descendente y marcar jornadas múltiples
      const sortedWorkdays = enrichedWorkdays.sort((a, b) => {
        if (a.date !== b.date) {
          return b.date.localeCompare(a.date);
        }
        return new Date(b.clock_in || 0) - new Date(a.clock_in || 0);
      });
      
      setWorkdays(sortedWorkdays);
    } catch (error) {
      console.error("Error fetching workdays:", error);
      toast.error(t("pages.punchRecords.errorLoading"));
      setWorkdays([]);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, startDate, endDate, users, workSchedules, t]);

  const fetchTimeRecords = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      
      if (selectedUser && selectedUser !== "all") {
        params.append("user_id", selectedUser);
      }
      if (startDate) {
        params.append("start_date", startDate);
      }
      if (endDate) {
        params.append("end_date", endDate);
      }
      
      const url = params.toString() 
        ? `${API_URL}/time-records/history?${params.toString()}`
        : `${API_URL}/time-records/history`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setTimeRecords(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching time records:", error);
    }
  }, [selectedUser, startDate, endDate]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      params.append("role", "vigilante");
      
      if (selectedCompany !== "all" && canSelectCompany()) {
        params.append("company_id", selectedCompany);
      } else if (!canSelectCompany() && user?.company_id) {
        params.append("company_id", user.company_id);
      }
      
      const response = await axios.get(`${API_URL}/users?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/companies`, {
        headers: getAuthHeaders(),
      });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchWorkSchedules = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCompany !== "all" && canSelectCompany()) {
        params.append("company_id", selectedCompany);
      } else if (!canSelectCompany() && user?.company_id) {
        params.append("company_id", user.company_id);
      }
      
      const response = await axios.get(`${API_URL}/work-schedules?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setWorkSchedules(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching work schedules:", error);
    }
  };

  useEffect(() => {
    if (canSelectCompany()) {
      fetchCompanies();
    }
    fetchWorkSchedules();
  }, [selectedCompany]);

  useEffect(() => {
    fetchUsers();
  }, [selectedCompany]);

  useEffect(() => {
    fetchWorkdays();
    fetchTimeRecords();
  }, [fetchWorkdays, fetchTimeRecords]);

  // Calcular análisis completo de un workday
  const analyzeWorkday = (workday) => {
    const workdayDate = new Date(workday.date);
    const dayOfWeek = workdayDate.getDay();
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[dayOfWeek];
    
    let scheduledStart = null;
    let scheduledEnd = null;
    let scheduledHours = null;
    
    if (workday.schedule?.days) {
      const daySchedule = workday.schedule.days.find(
        d => d.day_of_week === dayName && d.is_active
      );
      
      if (daySchedule) {
        scheduledStart = daySchedule.start_time;
        scheduledEnd = daySchedule.end_time;
        const [startHour, startMin] = scheduledStart.split(":").map(Number);
        const [endHour, endMin] = scheduledEnd.split(":").map(Number);
        scheduledHours = ((endHour * 60 + endMin) - (startHour * 60 + startMin)) / 60;
      }
    }

    // Calcular horas trabajadas: (Salida - Entrada) - tiempo de almuerzo
    let workedHours = null;
    let workedHoursFormatted = "-";
    if (workday.clock_in && workday.clock_out) {
      const clockIn = new Date(workday.clock_in);
      const clockOut = new Date(workday.clock_out);
      const totalMinutes = (clockOut - clockIn) / (1000 * 60);
      const lunchMinutes = workday.lunch_hours ? workday.lunch_hours * 60 : 0;
      workedHours = (totalMinutes - lunchMinutes) / 60;
      
      const hours = Math.floor(workedHours);
      const minutes = Math.round((workedHours - hours) * 60);
      workedHoursFormatted = `${hours}h ${minutes}m`;
    } else if (workday.clock_in) {
      // En curso - calcular horas hasta ahora
      const clockIn = new Date(workday.clock_in);
      const now = new Date();
      const totalMinutes = (now - clockIn) / (1000 * 60);
      const lunchMinutes = workday.lunch_hours ? workday.lunch_hours * 60 : 0;
      workedHours = (totalMinutes - lunchMinutes) / 60;
      
      const hours = Math.floor(workedHours);
      const minutes = Math.round((workedHours - hours) * 60);
      workedHoursFormatted = `${hours}h ${minutes}m`;
    }

    // Análisis de entrada
    let arrivalStatus = null;
    let arrivalDiff = null;
    let arrivalDiffFormatted = "-";
    if (workday.clock_in && scheduledStart) {
      const clockInDate = new Date(workday.clock_in);
      const [scheduledHour, scheduledMin] = scheduledStart.split(":").map(Number);
      const scheduledDate = new Date(workdayDate);
      scheduledDate.setHours(scheduledHour, scheduledMin, 0, 0);
      
      const diffMinutes = (clockInDate - scheduledDate) / (1000 * 60);
      arrivalDiff = diffMinutes;
      
      if (Math.abs(diffMinutes) <= 5) {
        arrivalStatus = "onTime";
        arrivalDiffFormatted = t("pages.punchRecords.onTime");
      } else if (diffMinutes > 5) {
        arrivalStatus = "late";
        const hours = Math.floor(Math.abs(diffMinutes) / 60);
        const mins = Math.round(Math.abs(diffMinutes) % 60);
        arrivalDiffFormatted = `${hours > 0 ? `${hours}h ` : ""}${mins}m ${t("pages.punchRecords.late")}`;
      } else {
        arrivalStatus = "early";
        const hours = Math.floor(Math.abs(diffMinutes) / 60);
        const mins = Math.round(Math.abs(diffMinutes) % 60);
        arrivalDiffFormatted = `${hours > 0 ? `${hours}h ` : ""}${mins}m ${t("pages.punchRecords.early")}`;
      }
    } else if (!workday.clock_in && scheduledStart) {
      arrivalStatus = "absent";
      arrivalDiffFormatted = t("pages.punchRecords.absent");
    }

    // Análisis de salida
    let departureStatus = null;
    let departureDiff = null;
    let departureDiffFormatted = "-";
    if (workday.clock_out && scheduledEnd) {
      const clockOutDate = new Date(workday.clock_out);
      const [scheduledHour, scheduledMin] = scheduledEnd.split(":").map(Number);
      const scheduledDate = new Date(workdayDate);
      scheduledDate.setHours(scheduledHour, scheduledMin, 0, 0);
      
      const diffMinutes = (clockOutDate - scheduledDate) / (1000 * 60);
      departureDiff = diffMinutes;
      
      if (Math.abs(diffMinutes) <= 5) {
        departureStatus = "onTime";
        departureDiffFormatted = t("pages.punchRecords.onTime");
      } else if (diffMinutes < -5) {
        departureStatus = "early";
        const hours = Math.floor(Math.abs(diffMinutes) / 60);
        const mins = Math.round(Math.abs(diffMinutes) % 60);
        departureDiffFormatted = `${hours > 0 ? `${hours}h ` : ""}${mins}m ${t("pages.punchRecords.early")}`;
      } else {
        departureStatus = "late";
        const hours = Math.floor(Math.abs(diffMinutes) / 60);
        const mins = Math.round(Math.abs(diffMinutes) % 60);
        departureDiffFormatted = `${hours > 0 ? `${hours}h ` : ""}${mins}m ${t("pages.punchRecords.late")}`;
      }
    }

    // Determinar estado general
    let status = "incomplete";
    let statusColor = "yellow";
    let statusText = t("pages.punchRecords.inProgress");
    let statusIcon = Clock;
    
    if (!workday.clock_in && scheduledStart) {
      status = "absent";
      statusColor = "red";
      statusText = t("pages.punchRecords.absent");
      statusIcon = AlertCircle;
    } else if (workday.clock_in && workday.clock_out) {
      // Si tiene entrada y salida, está completo
      status = "complete";
      statusColor = "green";
      statusText = t("pages.punchRecords.complete");
      statusIcon = CheckCircle2;
    } else if (workday.is_complete) {
      // También verificar el flag is_complete del backend
      status = "complete";
      statusColor = "green";
      statusText = t("pages.punchRecords.complete");
      statusIcon = CheckCircle2;
    } else if (workday.clock_in && !workday.clock_out) {
      status = "inProgress";
      statusColor = "blue";
      statusText = t("pages.punchRecords.inProgress");
      statusIcon = Clock;
    }

    // Detectar turno nocturno (entrada después de las 18:00 o salida antes de las 6:00)
    const isNightShift = workday.clock_in && (() => {
      const clockIn = new Date(workday.clock_in);
      const hour = clockIn.getHours();
      return hour >= 18 || hour < 6;
    })();

    // Detectar almuerzo no registrado
    const lunchNotRegistered = workday.clock_in && workday.clock_out && 
      (!workday.lunch_out || !workday.lunch_in) && workedHours && workedHours > 6;

    // Detectar más de X horas (configurable, por defecto 10)
    const maxHours = 10;
    const exceedsMaxHours = workedHours && workedHours > maxHours;

    return {
      scheduledStart,
      scheduledEnd,
      scheduledHours,
      workedHours,
      workedHoursFormatted,
      arrivalStatus,
      arrivalDiff,
      arrivalDiffFormatted,
      departureStatus,
      departureDiff,
      departureDiffFormatted,
      status,
      statusColor,
      statusText,
      statusIcon,
      isNightShift,
      lunchNotRegistered,
      exceedsMaxHours,
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "onTime":
        return "text-green-600 bg-green-50";
      case "late":
        return "text-red-600 bg-red-50";
      case "early":
        return "text-blue-600 bg-blue-50";
      case "absent":
        return "text-red-600 bg-red-50";
      default:
        return "text-slate-600 bg-slate-50";
    }
  };

  const getDayTimeRecords = (workday) => {
    return timeRecords.filter(tr => {
      const trDate = new Date(tr.timestamp).toISOString().split('T')[0];
      return tr.user_id === workday.user_id && trDate === workday.date;
    }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const openMapDialog = (location, title) => {
    if (location && location.latitude && location.longitude) {
      setMapLocation(location);
      setMapTitle(title);
      setIsMapDialogOpen(true);
    }
  };

  const handleSelectWorkday = (workday) => {
    setSelectedWorkday(workday);
    setIsDetailPanelOpen(true);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.punchRecords.title")}
          </h1>
          <p className="text-slate-600 mt-1">{t("pages.punchRecords.subtitle")}</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {canSelectCompany() && (
              <div className="space-y-2">
                <Label>{t("pages.punchRecords.company")}</Label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("pages.punchRecords.allCompanies")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.punchRecords.allCompanies")}</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{t("pages.punchRecords.guard")}</Label>
              <Select value={selectedUser || "all"} onValueChange={setSelectedUser}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.punchRecords.allGuards")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.punchRecords.allGuards")}</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("pages.punchRecords.from")}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("pages.punchRecords.to")}</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="flex items-end">
              <Button onClick={() => { fetchWorkdays(); fetchTimeRecords(); }} className="w-full">
                <Calendar className="h-4 w-4 mr-2" />
                {t("common.filter")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de registros */}
      <div className="flex gap-6">
        <Card className="flex-1">
          <CardHeader>
            <CardTitle>{t("pages.punchRecords.records")}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <p>{t("common.loading")}</p>
              </div>
            ) : workdays.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">{t("pages.punchRecords.noRecords")}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("pages.punchRecords.guard")}</TableHead>
                      <TableHead>{t("pages.punchRecords.date")}</TableHead>
                      <TableHead>{t("pages.punchRecords.clockIn")}</TableHead>
                      <TableHead>{t("pages.punchRecords.lunchOut")}</TableHead>
                      <TableHead>{t("pages.punchRecords.lunchIn")}</TableHead>
                      <TableHead>{t("pages.punchRecords.clockOut")}</TableHead>
                      <TableHead>{t("pages.punchRecords.workedHours")}</TableHead>
                      <TableHead>{t("pages.punchRecords.arrival")}</TableHead>
                      <TableHead>{t("pages.punchRecords.expected")}</TableHead>
                      <TableHead>{t("pages.punchRecords.difference")}</TableHead>
                      <TableHead>{t("pages.punchRecords.status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workdays.map((workday) => {
                      const analysis = analyzeWorkday(workday);
                      const StatusIcon = analysis.statusIcon;
                      
                      return (
                        <TableRow 
                          key={workday.id}
                          className={cn(
                            "cursor-pointer hover:bg-slate-50",
                            selectedWorkday?.id === workday.id && "bg-blue-50"
                          )}
                          onClick={() => handleSelectWorkday(workday)}
                        >
                          <TableCell className="font-medium">
                            {workday.user?.name || t("pages.punchRecords.unknown")}
                          </TableCell>
                          <TableCell>
                            {new Date(workday.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {workday.clock_in ? formatTime(workday.clock_in) : "-"}
                          </TableCell>
                          <TableCell>
                            {workday.lunch_out ? formatTime(workday.lunch_out) : "-"}
                          </TableCell>
                          <TableCell>
                            {workday.lunch_in ? formatTime(workday.lunch_in) : "-"}
                          </TableCell>
                          <TableCell>
                            {workday.clock_out ? formatTime(workday.clock_out) : "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{analysis.workedHoursFormatted}</span>
                              {analysis.exceedsMaxHours && (
                                <span className="text-red-600" title={t("pages.punchRecords.exceedsMaxHours")}>🟥</span>
                              )}
                              {analysis.lunchNotRegistered && (
                                <span className="text-yellow-600" title={t("pages.punchRecords.lunchNotRegistered")}>🟨</span>
                              )}
                              {analysis.isNightShift && (
                                <span className="text-blue-600" title={t("pages.punchRecords.nightShift")}>🟦</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {analysis.arrivalStatus ? (
                              <span className={cn("px-2 py-1 rounded text-xs font-medium", getStatusColor(analysis.arrivalStatus))}>
                                {analysis.arrivalDiffFormatted}
                              </span>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            {analysis.scheduledStart || "-"}
                          </TableCell>
                          <TableCell>
                            {analysis.arrivalDiff !== null ? (
                              <span className={cn("px-2 py-1 rounded text-xs font-medium", getStatusColor(analysis.arrivalStatus))}>
                                {(() => {
                                  const diffMinutes = Math.abs(analysis.arrivalDiff);
                                  const hours = Math.floor(diffMinutes / 60);
                                  const minutes = Math.round(diffMinutes % 60);
                                  const sign = analysis.arrivalDiff > 0 ? "+" : "-";
                                  return `${sign}${hours > 0 ? `${hours}h ` : ""}${minutes}m`;
                                })()}
                              </span>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={cn(
                              "inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium",
                              analysis.statusColor === "green" && "text-green-600 bg-green-50",
                              analysis.statusColor === "red" && "text-red-600 bg-red-50",
                              analysis.statusColor === "yellow" && "text-yellow-600 bg-yellow-50",
                              analysis.statusColor === "blue" && "text-blue-600 bg-blue-50"
                            )}>
                              <StatusIcon className="h-3 w-3" />
                              {analysis.statusText}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Panel lateral de detalles */}
        {isDetailPanelOpen && selectedWorkday && (
          <Card className="w-96">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t("pages.punchRecords.details")}</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsDetailPanelOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Información básica */}
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-slate-500">{t("pages.punchRecords.guard")}</Label>
                  <p className="font-medium">{selectedWorkday.user?.name || t("pages.punchRecords.unknown")}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{t("pages.punchRecords.date")}</Label>
                  <p className="font-medium">{new Date(selectedWorkday.date).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Ubicaciones */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t("pages.punchRecords.locations")}</Label>
                {selectedWorkday.clock_in_location && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => openMapDialog(
                      selectedWorkday.clock_in_location,
                      `${t("pages.punchRecords.clockIn")} - ${formatTime(selectedWorkday.clock_in)}`
                    )}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    {t("pages.punchRecords.clockIn")}
                  </Button>
                )}
                {selectedWorkday.lunch_out_location && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => openMapDialog(
                      selectedWorkday.lunch_out_location,
                      `${t("pages.punchRecords.lunchOut")} - ${formatTime(selectedWorkday.lunch_out)}`
                    )}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    {t("pages.punchRecords.lunchOut")}
                  </Button>
                )}
                {selectedWorkday.lunch_in_location && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => openMapDialog(
                      selectedWorkday.lunch_in_location,
                      `${t("pages.punchRecords.lunchIn")} - ${formatTime(selectedWorkday.lunch_in)}`
                    )}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    {t("pages.punchRecords.lunchIn")}
                  </Button>
                )}
                {selectedWorkday.clock_out_location && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => openMapDialog(
                      selectedWorkday.clock_out_location,
                      `${t("pages.punchRecords.clockOut")} - ${formatTime(selectedWorkday.clock_out)}`
                    )}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    {t("pages.punchRecords.clockOut")}
                  </Button>
                )}
              </div>

              {/* Historial del día */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t("pages.punchRecords.dayHistory")}</Label>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {getDayTimeRecords(selectedWorkday).map((record, idx) => (
                    <div key={idx} className="text-xs p-2 bg-slate-50 rounded">
                      <div className="flex items-center gap-2">
                        {record.punch_type === "clock_in" && <LogIn className="h-3 w-3 text-green-600" />}
                        {record.punch_type === "lunch_out" && <Coffee className="h-3 w-3 text-yellow-600" />}
                        {record.punch_type === "lunch_in" && <Coffee className="h-3 w-3 text-blue-600" />}
                        {record.punch_type === "clock_out" && <LogOut className="h-3 w-3 text-red-600" />}
                        <span className="font-medium">
                          {record.punch_type === "clock_in" && t("pages.punchRecords.clockIn")}
                          {record.punch_type === "lunch_out" && t("pages.punchRecords.lunchOut")}
                          {record.punch_type === "lunch_in" && t("pages.punchRecords.lunchIn")}
                          {record.punch_type === "clock_out" && t("pages.punchRecords.clockOut")}
                          {!["clock_in", "lunch_out", "lunch_in", "clock_out"].includes(record.punch_type) && record.punch_type}
                        </span>
                        <span className="text-slate-500">{formatTime(record.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Botón de ajustar (solo supervisor) */}
              {canEdit() && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    // Inicializar el formulario con los valores actuales
                    const workdayDate = new Date(selectedWorkday.date);
                    setAdjustFormData({
                      clock_in: selectedWorkday.clock_in 
                        ? new Date(selectedWorkday.clock_in).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
                        : "",
                      lunch_out: selectedWorkday.lunch_out
                        ? new Date(selectedWorkday.lunch_out).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
                        : "",
                      lunch_in: selectedWorkday.lunch_in
                        ? new Date(selectedWorkday.lunch_in).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
                        : "",
                      clock_out: selectedWorkday.clock_out
                        ? new Date(selectedWorkday.clock_out).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
                        : "",
                      notes: selectedWorkday.adjustment_notes || "",
                    });
                    setIsAdjustDialogOpen(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t("pages.punchRecords.adjust")}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog del mapa */}
      <Dialog open={isMapDialogOpen} onOpenChange={setIsMapDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {mapTitle}
            </DialogTitle>
          </DialogHeader>
          {mapLocation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600">{t("pages.punchRecords.coordinates")}</p>
                  <p className="font-mono text-xs">
                    {mapLocation.latitude?.toFixed(6)}, {mapLocation.longitude?.toFixed(6)}
                  </p>
                </div>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.open(
                        `https://www.google.com/maps?q=${mapLocation.latitude},${mapLocation.longitude}`,
                        "_blank"
                      );
                    }}
                  >
                    {t("pages.punchRecords.openInGoogleMaps")}
                  </Button>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height: "500px" }}>
                <MapContainer
                  center={[mapLocation.latitude, mapLocation.longitude]}
                  zoom={18}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  />
                  <Marker
                    position={[mapLocation.latitude, mapLocation.longitude]}
                    icon={createPunchIcon("#3B82F6")}
                  >
                    <Popup>
                      <div className="p-2">
                        <p className="font-bold text-blue-600">{mapTitle}</p>
                        <p className="text-xs font-mono text-slate-500">
                          {mapLocation.latitude?.toFixed(6)}, {mapLocation.longitude?.toFixed(6)}
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de ajuste */}
      <Dialog open={isAdjustDialogOpen} onOpenChange={setIsAdjustDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("pages.punchRecords.adjust")}</DialogTitle>
            <DialogDescription>
              {t("pages.punchRecords.adjustDescription")}
            </DialogDescription>
          </DialogHeader>
          {selectedWorkday && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("pages.punchRecords.clockIn")}</Label>
                  <TimePicker
                    value={adjustFormData.clock_in}
                    onChange={(value) => setAdjustFormData({ ...adjustFormData, clock_in: value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("pages.punchRecords.lunchOut")}</Label>
                  <TimePicker
                    value={adjustFormData.lunch_out}
                    onChange={(value) => setAdjustFormData({ ...adjustFormData, lunch_out: value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("pages.punchRecords.lunchIn")}</Label>
                  <TimePicker
                    value={adjustFormData.lunch_in}
                    onChange={(value) => setAdjustFormData({ ...adjustFormData, lunch_in: value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("pages.punchRecords.clockOut")}</Label>
                  <TimePicker
                    value={adjustFormData.clock_out}
                    onChange={(value) => setAdjustFormData({ ...adjustFormData, clock_out: value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("pages.punchRecords.notes")}</Label>
                <Textarea
                  value={adjustFormData.notes}
                  onChange={(e) => setAdjustFormData({ ...adjustFormData, notes: e.target.value })}
                  placeholder={t("pages.punchRecords.notesPlaceholder")}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAdjustDialogOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={async () => {
                try {
                  const workdayDate = new Date(selectedWorkday.date);
                  const updateData = {};
                  
                  if (adjustFormData.clock_in) {
                    const [hours, minutes] = adjustFormData.clock_in.split(":").map(Number);
                    const clockInDate = new Date(workdayDate);
                    clockInDate.setHours(hours, minutes, 0, 0);
                    updateData.clock_in = clockInDate.toISOString();
                  }
                  
                  if (adjustFormData.lunch_out) {
                    const [hours, minutes] = adjustFormData.lunch_out.split(":").map(Number);
                    const lunchOutDate = new Date(workdayDate);
                    lunchOutDate.setHours(hours, minutes, 0, 0);
                    updateData.lunch_out = lunchOutDate.toISOString();
                  }
                  
                  if (adjustFormData.lunch_in) {
                    const [hours, minutes] = adjustFormData.lunch_in.split(":").map(Number);
                    const lunchInDate = new Date(workdayDate);
                    lunchInDate.setHours(hours, minutes, 0, 0);
                    updateData.lunch_in = lunchInDate.toISOString();
                  }
                  
                  if (adjustFormData.clock_out) {
                    const [hours, minutes] = adjustFormData.clock_out.split(":").map(Number);
                    const clockOutDate = new Date(workdayDate);
                    clockOutDate.setHours(hours, minutes, 0, 0);
                    updateData.clock_out = clockOutDate.toISOString();
                  }
                  
                  if (adjustFormData.notes) {
                    updateData.notes = adjustFormData.notes;
                  }
                  
                  await axios.put(
                    `${API_URL}/time-records/workdays/${selectedWorkday.id}`,
                    updateData,
                    { headers: getAuthHeaders() }
                  );
                  
                  toast.success(t("pages.punchRecords.adjustSuccess"));
                  setIsAdjustDialogOpen(false);
                  fetchWorkdays();
                  fetchTimeRecords();
                } catch (error) {
                  console.error("Error adjusting workday:", error);
                  toast.error(error.response?.data?.detail || t("pages.punchRecords.adjustError"));
                }
              }}
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
